import React, { useState, useEffect } from "react";
import { Steps, Button, message, Spin, Tooltip } from "antd";
import { useHistory, useParams, useLocation } from "react-router";
import { useSelector } from "react-redux";
import CommonDivWidget from "components/CommonDivWidget/CommonDivWidget";
import "./RegistrationWizard.css";
import Step1GeneralInfo from "./Steps/Step1GeneralInfo";
import Step2CheckoutSettings from "./Steps/Step2CheckoutSettings";
import Step3FormTemplate from "./Steps/Step3FormTemplate";
import Step4RegistrationOptions from "./Steps/Step4RegistrationOptions";
import Step5ExtraPrograms from "./Steps/Step5ExtraPrograms";
import Step6Waivers from "./Steps/Step6Waivers";
import { ROUTE_DASHBOARD } from "navigation/CONSTANTS";
import {
  apiGetActivityList,
  apiGetRegistrationSettings,
  apiGetRegistrationList,
  apiSaveWizardRegistration,
  apiSaveRegistrationGeneral
} from "services/dashboardService";
import { apiGetClubSite } from "services/customWebpageService";

const { Step } = Steps;

const RegistrationWizard = () => {
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const userDataStore = useSelector((x) => x.userDataStore);
  const appDataStore = useSelector((x) => x.appDataStore);

  const [currentStep, setCurrentStep] = useState(0);
  const [isBusy, setIsBusy] = useState(false);
  const [registrationID, setRegistrationID] = useState("");
  const [activityList, setActivityList] = useState([]);
  const [settingInfo, setSettingInfo] = useState(undefined);
  const [existingRegistrations, setExistingRegistrations] = useState([]);
  const [isCloning, setIsCloning] = useState(false);
  const [clubSiteDomain, setClubSiteDomain] = useState("");

  // Wizard data state
  const [wizardData, setWizardData] = useState({
    // Step 1: General Information
    name: "",
    description: "",
    registration_type: "individual", // 'individual' or 'facility'
    activity: undefined,
    sport_id: "",
    sport: "",
    admin_email: "",
    webpage_group: "",
    infoPageFile: null,

    // Step 2: Checkout Settings
    merchant: undefined,
    credit_fee: "",
    debit_fee: "",
    allow_echeck: false,
    echeck_fee: "",
    allow_manual_payments: false,
    email_invoices_to_admin: false,
    discodeList: [],

    // Step 3: Form Template
    formTemplateOption: "default", // "default" or "copy"
    copyFromRegistrationId: undefined,

    // Step 4-6: Will be populated by respective components
  });

  useEffect(() => {
    loadActivityList();
    loadExistingRegistrations();

    // Check if cloning from existing registration
    const searchParams = new URLSearchParams(location.search);
    const cloneFromId = searchParams.get('clone');

    if (cloneFromId) {
      setIsCloning(true);
      loadCloneData(cloneFromId);
    } else if (id != undefined) {
      setRegistrationID(id);
      loadSettingInfo(id);
    }
  }, [id, location.search]);

  // Fetch club site domain
  useEffect(() => {
    const clubID = appDataStore?.club?.id;
    if (clubID && userDataStore.token) {
      apiGetClubSite({ token: userDataStore.token, club_id: clubID })
        .then((res) => {
          if (res && res.domain) {
            setClubSiteDomain(res.domain);
          }
        })
        .catch((err) => {
          console.error("Failed to load club site:", err);
        });
    }
  }, [appDataStore?.club?.id, userDataStore.token]);

  const loadActivityList = async () => {
    const params = new URLSearchParams();
    params.append("token", userDataStore.token);
    try {
      const res = await apiGetActivityList();
      if (res != undefined) {
        setActivityList(res);
      } else {
        setActivityList([]);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const loadExistingRegistrations = async () => {
    const params = new URLSearchParams();
    params.append("token", userDataStore.token);
    params.append("club_id", appDataStore?.club?.id);
    try {
      const registrations = await apiGetRegistrationList(params);
      setExistingRegistrations(registrations || []);
    } catch (err) {
      console.log(err);
    }
  };

  const loadSettingInfo = async (regID) => {
    setIsBusy(true);
    const params = new URLSearchParams();
    params.append("token", userDataStore.token);
    params.append("registration_id", regID);
    if (appDataStore?.club?.id) params.append("club_id", appDataStore.club.id);
    if (userDataStore?.sport_type) params.append("sport_type", userDataStore.sport_type);
    try {
      const res = await apiGetRegistrationSettings(params);
      setSettingInfo(res);
      setIsBusy(false);
    } catch (err) {
      setIsBusy(false);
      message.error(err);
    }
  };

  const loadCloneData = async (cloneFromId) => {
    setIsBusy(true);
    const params = new URLSearchParams();
    params.append("token", userDataStore.token);
    params.append("registration_id", cloneFromId);
    if (appDataStore?.club?.id) params.append("club_id", appDataStore.club.id);
    if (userDataStore?.sport_type) params.append("sport_type", userDataStore.sport_type);

    try {
      const cloneData = await apiGetRegistrationSettings(params);

      if (cloneData) {
        // get_registration_settings returns registration_info and discount_codes
        const srcReg = cloneData.registration_info || {};
        const srcCodes = cloneData.discount_codes || [];

        // Transform discount codes from DB format to wizard UI format
        const mappedDiscodeList = srcCodes.map((code) => ({
          text: code.discount_code || "",
          type: code.type || "single",
          percentage: code.discount_percentage || "",
          amount: code.discount_amount || "",
          free: code.discount_free == 1,
          visible: 1,
          row_index: code.row_index || "",
          multipleList: [2, 3, 4, 5, 6],
        }));

        // Create a new registration immediately by cloning
        const saveParams = new URLSearchParams();
        saveParams.append("token", userDataStore.token);
        saveParams.append("club_id", appDataStore?.club?.id);

        // Copy all data from source registration
        saveParams.append("name", "(Clone) " + (srcReg.registration_name || ""));
        saveParams.append("description", srcReg.registration_description || "");
        saveParams.append("sport_id", srcReg.club_sport_id || "");
        saveParams.append("sport", srcReg.club_sport || "");
        saveParams.append("admin_email", srcReg.admin_email || "");
        saveParams.append("webpage_group", srcReg.webpage_group || "");
        saveParams.append("merchant", srcReg.merchant || "");
        saveParams.append("credit_fee", srcReg.credit_fee || "");
        saveParams.append("debit_fee", srcReg.debit_fee || "");
        saveParams.append(
          "allow_echeck",
          srcReg.echeck_fee && srcReg.echeck_fee !== "0" ? "1" : "0"
        );
        saveParams.append("echeck_fee", srcReg.echeck_fee || "");
        saveParams.append(
          "allow_manual_payments",
          srcReg.allow_manual_payments === "1" || srcReg.allow_manual_payments === 1 ? "1" : "0"
        );
        saveParams.append(
          "email_invoices_to_admin",
          srcReg.email_invoices_to_admin === "1" || srcReg.email_invoices_to_admin === 1 ? "1" : "0"
        );
        saveParams.append("discount_code_list", JSON.stringify(mappedDiscodeList));
        saveParams.append("form_template_option", "copy");
        saveParams.append("copy_from_registration_id", cloneFromId);

        const response = await apiSaveWizardRegistration(saveParams);

        if (response && response.registration_id) {
          const newRegId = response.registration_id;
          setRegistrationID(newRegId);

          // Load the newly created registration's settings for Steps 4-6
          await loadSettingInfo(newRegId);

          // Pre-populate wizard data for Steps 1-3
          setWizardData({
            // Step 1: Name with "(Clone)" prefix for user to edit
            name: "(Clone) " + (srcReg.registration_name || ""),
            description: srcReg.registration_description || "",
            activity: srcReg.club_sport_id ? parseInt(srcReg.club_sport_id) : undefined,
            sport_id: srcReg.club_sport_id || "",
            sport: srcReg.club_sport || "",
            admin_email: srcReg.admin_email || "",
            webpage_group: srcReg.webpage_group || "",
            infoPageFile: null,

            // Step 2: Copy checkout settings
            merchant: srcReg.merchant || undefined,
            credit_fee: srcReg.credit_fee || "",
            debit_fee: srcReg.debit_fee || "",
            allow_echeck: !!(srcReg.echeck_fee && srcReg.echeck_fee !== "0"),
            echeck_fee: srcReg.echeck_fee || "",
            allow_manual_payments: srcReg.allow_manual_payments === "1" || srcReg.allow_manual_payments === 1,
            email_invoices_to_admin: srcReg.email_invoices_to_admin === "1" || srcReg.email_invoices_to_admin === 1,
            discodeList: mappedDiscodeList,

            // Step 3: Automatically set to copy from this registration
            formTemplateOption: "copy",
            copyFromRegistrationId: cloneFromId,
          });

          message.success("Registration cloned successfully. Please update the name and review all settings.");
        } else {
          message.error("Failed to clone registration");
        }
      }
    } catch (err) {
      console.error("Error loading clone data:", err);
      message.error("Failed to load registration settings");
    } finally {
      setIsBusy(false);
    }
  };

  const steps = [
    {
      title: "General Information",
      shortTitle: "General Info",
      content: (
        <Step1GeneralInfo
          wizardData={wizardData}
          setWizardData={setWizardData}
          activityList={activityList}
          allRegistrations={existingRegistrations}
        />
      ),
    },
    {
      title: "Registration Form",
      shortTitle: "Form",
      content: (
        <Step3FormTemplate
          wizardData={wizardData}
          setWizardData={setWizardData}
          existingRegistrations={existingRegistrations}
          registrationID={registrationID}
        />
      ),
    },
    {
      title: "Registration Options",
      shortTitle: "Options",
      content: (
        <Step4RegistrationOptions
          wizardData={wizardData}
          setWizardData={setWizardData}
          registrationID={registrationID}
          settingInfo={settingInfo}
        />
      ),
    },
    {
      title: "Extra Programs",
      shortTitle: "Programs",
      content: (
        <Step5ExtraPrograms
          wizardData={wizardData}
          setWizardData={setWizardData}
          registrationID={registrationID}
          settingInfo={settingInfo}
        />
      ),
    },
    {
      title: "Waivers",
      shortTitle: "Waivers",
      content: (
        <Step6Waivers
          wizardData={wizardData}
          setWizardData={setWizardData}
          registrationID={registrationID}
          settingInfo={settingInfo}
        />
      ),
    },
    {
      title: "Checkout Settings",
      shortTitle: "Checkout",
      content: (
        <Step2CheckoutSettings
          wizardData={wizardData}
          setWizardData={setWizardData}
        />
      ),
    },
  ];

  const validateStep = (step) => {
    switch (step) {
      case 0: // Step 1: General Information
        if (!wizardData.name || !wizardData.name.trim()) {
          message.error("Please enter a registration name");
          return false;
        }
        if (!wizardData.activity) {
          message.error("Please select an activity type");
          return false;
        }
        if (!wizardData.admin_email || !wizardData.admin_email.trim()) {
          message.error("Please enter an admin email address");
          return false;
        }
        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(wizardData.admin_email)) {
          message.error("Please enter a valid email address");
          return false;
        }
        break;
      case 1: // Step 2: Registration Form
        if (!wizardData.formTemplateOption) {
          message.error("Please select a form template option");
          return false;
        }
        if (wizardData.formTemplateOption === "copy" && (wizardData.copyFromRegistrationId === undefined || wizardData.copyFromRegistrationId === null)) {
          message.error("Please select a registration to copy from");
          return false;
        }
        break;
      case 2: // Step 3: Registration Options
        // Add validations for registration options if needed
        break;
      case 3: // Step 4: Extra Programs
        // Add validations for extra programs if needed
        break;
      case 4: // Step 5: Waivers
        // Add validations for waivers if needed
        break;
      case 5: // Step 6: Checkout Settings
        if (!wizardData.merchant) {
          message.error("Please select a merchant");
          return false;
        }
        if (!wizardData.credit_fee || wizardData.credit_fee === "") {
          message.error("Please enter a credit card fee");
          return false;
        }
        if (!wizardData.debit_fee || wizardData.debit_fee === "") {
          message.error("Please enter a debit card fee");
          return false;
        }
        if (wizardData.allow_echeck && (!wizardData.echeck_fee || wizardData.echeck_fee === "")) {
          message.error("Please enter an eCheck fee");
          return false;
        }
      default:
        break;
    }
    return true;
  };

  const next = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  // Save Step 1 (General Info) and proceed to next step
  const handleSaveStep1AndNext = async () => {
    if (!validateStep(0)) {
      return;
    }

    // If we already have a registrationID (edit mode or clone mode), just proceed
    if (registrationID) {
      setCurrentStep(currentStep + 1);
      return;
    }

    setIsBusy(true);

    try {
      const params = new URLSearchParams();
      params.append("token", userDataStore.token);
      params.append("club_id", appDataStore?.club?.id);
      params.append("name", wizardData.name);
      params.append("registration_type", wizardData.registration_type || "individual");
      params.append("parent_registration_id", wizardData.parent_registration_id || "");
      params.append("sport_id", wizardData.sport_id);
      params.append("sport", wizardData.sport);
      params.append("admin_email", wizardData.admin_email);
      params.append("webpage_group", wizardData.webpage_group || "");
      if (clubSiteDomain) {
        params.append("domain", clubSiteDomain);
      }
      // Include checkout settings with defaults for new registrations
      params.append("merchant", wizardData.merchant || "stax");
      params.append("credit_fee", wizardData.credit_fee || "0");
      params.append("debit_fee", wizardData.debit_fee || "0");
      params.append("echeck_fee", wizardData.echeck_fee || "0");

      const response = await apiSaveRegistrationGeneral(params);

      if (response && response.registration_id) {
        setRegistrationID(response.registration_id);
        // Load the settings for the newly created registration
        await loadSettingInfo(response.registration_id);
        message.success("Registration saved. Proceeding to next step.");
        setCurrentStep(currentStep + 1);
      } else {
        message.error("Failed to save registration. Please try again.");
      }
    } catch (error) {
      console.error("Error saving Step 1:", error);
      message.error("An error occurred while saving the registration");
    } finally {
      setIsBusy(false);
    }
  };

  const prev = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleCancel = () => {
    if (window.confirm("Are you sure you want to cancel? All unsaved progress will be lost.")) {
      history.push(ROUTE_DASHBOARD);
    }
  };

  const handleFinish = async () => {
    // Final validation
    if (!validateStep(0) || !validateStep(1) || !validateStep(2)) {
      message.error("Please complete all required fields in steps 1-3");
      return;
    }

    setIsBusy(true);

    try {
      // Use FormData to support file uploads (info page file)
      const params = new FormData();
      params.append("token", userDataStore.token);
      params.append("club_id", appDataStore?.club?.id);

      // Include registration_id to UPDATE the existing registration (created in Step 1)
      if (registrationID) {
        params.append("registration_id", registrationID);
      }

      // Step 1 data
      params.append("name", wizardData.name);
      params.append("description", wizardData.description || "");
      params.append("registration_type", wizardData.registration_type || "individual");
      params.append("no_registration_required", wizardData.no_registration_required ? "1" : "0");
      if (clubSiteDomain) {
        params.append("domain", clubSiteDomain);
      }
      params.append("sport_id", wizardData.sport_id);
      params.append("sport", wizardData.sport);
      params.append("admin_email", wizardData.admin_email);
      params.append("webpage_group", wizardData.webpage_group || "");

      // Info page file
      if (wizardData.infoPageFile instanceof File) {
        params.append("home_info", wizardData.infoPageFile);
      }

      // Step 2 data
      params.append("merchant", wizardData.merchant);
      params.append("credit_fee", wizardData.credit_fee);
      params.append("debit_fee", wizardData.debit_fee);
      params.append("allow_echeck", wizardData.allow_echeck ? "1" : "0");
      params.append("echeck_fee", wizardData.echeck_fee || "");
      params.append("allow_manual_payments", wizardData.allow_manual_payments ? "1" : "0");
      params.append("email_invoices_to_admin", wizardData.email_invoices_to_admin ? "1" : "0");
      params.append("discount_code_list", JSON.stringify(wizardData.discodeList || []));

      // Step 3 data - only include if NOT cloning (cloning already set this up)
      if (!isCloning) {
        params.append("form_template_option", wizardData.formTemplateOption);
        params.append("copy_from_registration_id", wizardData.copyFromRegistrationId || "");
      }

      const response = await apiSaveWizardRegistration(params);

      if (response && response.registration_id) {
        message.success(isCloning ? "Registration updated successfully!" : "Registration created successfully!");
        // Redirect to the dashboard
        history.push(ROUTE_DASHBOARD);
      } else {
        message.error(isCloning ? "Failed to update registration. Please try again." : "Failed to create registration. Please try again.");
      }
    } catch (error) {
      console.error("Error saving wizard registration:", error);
      message.error("An error occurred while saving the registration");
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <CommonDivWidget>
      <div className="registration-wizard">
        <Spin spinning={isBusy}>
          <div className="wizard-header">
            <h2>{isCloning ? "Clone Registration Setup Wizard" : "New Registration Setup Wizard"}</h2>
            <p>
              {isCloning
                ? "Settings have been copied. Please provide a new name and description, then review other settings."
                : "Follow these steps to quickly set up your new registration"}
            </p>
          </div>

          <Steps current={currentStep} className="wizard-steps">
            {steps.map((item, index) => (
              <Step 
                key={index} 
                title={
                  <Tooltip title={item.title} placement="top">
                    <span>{item.shortTitle || item.title}</span>
                  </Tooltip>
                } 
              />
            ))}
          </Steps>

          <div className="steps-content">{steps[currentStep].content}</div>

          <div className="steps-action">
            <Button onClick={handleCancel} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            {currentStep > 0 && (
              <Button onClick={prev} style={{ marginRight: 8 }}>
                Back
              </Button>
            )}
            {currentStep === 0 && !registrationID && (
              <Button type="primary" onClick={handleSaveStep1AndNext}>
                Save and Next
              </Button>
            )}
            {currentStep === 0 && registrationID && (
              <Button type="primary" onClick={next}>
                Next
              </Button>
            )}
            {currentStep > 0 && currentStep < steps.length - 1 && (
              <Button type="primary" onClick={next}>
                Next
              </Button>
            )}
            {currentStep === steps.length - 1 && (
              <Button type="primary" onClick={handleFinish}>
                Finish
              </Button>
            )}
          </div>
        </Spin>
      </div>
    </CommonDivWidget>
  );
};

export default RegistrationWizard;
